import {
    shipDrag, shipAcceleration, shipMaxVelocity, shipAngularVelocity,
    shipBounceRestitution, laserLifespan, laserSpeed, laserGap, shieldDuration
} from './constants';
import type { LaserImage } from './constants';
import type { Level } from './Level';

// The player's ship: its creation, the keyboard/gamepad input -> movement, the
// thrust/rotate/fire sounds, firing lasers, and the ship-side collision response
// (precise swept polygon detection + manual bounce, taking damage, destruction).
// The game-state counters it touches - alive / shield / nextHit / laserInterval -
// stay on Level (the scene loop and HUD read them), reached via `level`.
export class PlayerShip {
    sprite: Phaser.Physics.Arcade.Sprite;
    lasers: Phaser.Physics.Arcade.Group;

    private cursors: Phaser.Types.Input.Keyboard.CursorKeys;
    private fireKey: Phaser.Input.Keyboard.Key;
    private startWasDown: boolean = false; // gamepad Start button edge-detection (pause)
    private polygonBase: Phaser.Geom.Point[];

    // Pose from the previous frame, used to sweep collision detection so a fast
    // ship cannot tunnel through an obstacle between two frames.
    private prevX!: number;
    private prevY!: number;
    private prevAngle!: number;

    sfx_thrust: Phaser.Sound.BaseSound;
    sfx_left: Phaser.Sound.BaseSound;
    sfx_right: Phaser.Sound.BaseSound;
    sfx_laser: Phaser.Sound.BaseSound;
    sfx_collide: Phaser.Sound.BaseSound;
    sfx_explode: Phaser.Sound.BaseSound;

    constructor(private level: Level) {
        const physics = level.physics;

        this.sprite = physics.add.sprite(physics.world.bounds.centerX, physics.world.bounds.centerY, 'ship');
        this.sprite.angle = -90;
        this.sprite.setOrigin(0.5, 0.5);

        level.anims.create({
            key: 'ignite',
            frames: level.anims.generateFrameNumbers('ship', { start: 0, end: 9 }),
            frameRate: 30
        });

        level.anims.create({
            key: 'explode',
            frames: [
                { key: 'ship', frame: 0 },
                { key: 'ship', frame: 10 },
                { key: 'ship', frame: 11 },
                { key: 'ship', frame: 12 },
                { key: 'ship', frame: 13 },
                { key: 'ship', frame: 14 }
            ],
            frameRate: 15
        });

        this.lasers = physics.add.group({
            classType: Phaser.Physics.Arcade.Image,
            maxSize: 30,
            runChildUpdate: true
        });

        this.sprite.setDrag(shipDrag);
        this.sprite.setMaxVelocity(shipMaxVelocity);
        this.sprite.setCollideWorldBounds(true);
        this.sprite.setBounce(1, 1);
        this.sprite.setAngularDrag(shipDrag);

        this.cursors = level.input.keyboard.createCursorKeys();
        this.fireKey = level.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

        this.sfx_thrust = level.sound.add('thrust');
        this.sfx_left = level.sound.add('left');
        this.sfx_right = level.sound.add('right');
        this.sfx_laser = level.sound.add('laser');
        this.sfx_collide = level.sound.add('collide');
        this.sfx_explode = level.sound.add('explode');

        // Ship polygon - roughly matching the ship's triangular shape
        this.polygonBase = [
            new Phaser.Geom.Point(-40, 0),    // left center
            new Phaser.Geom.Point(-15, -20),  // left top
            new Phaser.Geom.Point(50, 0),     // right center (nose)
            new Phaser.Geom.Point(-15, 20)    // left bottom
        ];
    }

    setSfxVolume(volume: number) {
        const sounds: any[] = [
            this.sfx_thrust, this.sfx_left, this.sfx_right,
            this.sfx_laser, this.sfx_collide, this.sfx_explode
        ];
        for (let i = 0; i < sounds.length; i++) {
            if (sounds[i]) {
                sounds[i].setVolume(volume);
            }
        }
    }

    // Stop the looping ship sounds (used on pause / level-up / destruction).
    silence() {
        this.sfx_thrust.stop();
        this.sfx_left.stop();
        this.sfx_right.stop();
    }

    handleInput(time: number) {
        // Gather the intended actions from BOTH the keyboard and the gamepad
        // first, then apply them ONCE below. (Previously these were two separate
        // blocks: on the controller the keyboard block's "else" branches stopped
        // the thrust/rotate sounds every frame and the gamepad block restarted
        // them - so a held control was treated as a fresh press each frame and
        // the looping thrust sound never sustained.)
        let angular = 0;     // -1..1, scaled to shipAngularVelocity
        let thrust = false;
        let fire = false;

        // Keyboard
        if (this.cursors.left.isDown) {
            angular = -1;
        } else if (this.cursors.right.isDown) {
            angular = 1;
        }
        if (this.cursors.up.isDown) {
            thrust = true;
        }
        if (this.fireKey.isDown) {
            fire = true;
        }

        // Gamepad (if connected) - adds to / overrides the keyboard intent.
        const input = this.level.input;
        if (input && input.gamepad && input.gamepad.total > 0) {
            const pad = input.gamepad.getPad(0);
            if (pad) {
                // Read controls defensively: simple USB controllers (e.g. a
                // Quickshot joystick) expose far fewer axes/buttons than the
                // standard mapping, so a missing index must NOT throw - that used
                // to hang the game the instant such a pad was plugged in.
                const btn = (i: number) => pad.buttons[i]?.pressed ?? false;
                const btnVal = (i: number) => pad.buttons[i]?.value ?? 0;
                const axis = (i: number) => pad.axes[i]?.getValue() ?? 0;

                // Different controllers need different mappings. The native
                // gamepad's `mapping` is "standard" for Xbox/PS-style pads and ""
                // for simple/legacy joysticks (e.g. the Quickshot); fall back to a
                // button-count heuristic if the native pad isn't exposed.
                const standard = pad.pad ? (pad.pad.mapping === 'standard') : (pad.buttons.length >= 8);

                // Rotation: left stick / joystick X - same on both controllers.
                const stickX = axis(0);
                if (Math.abs(stickX) > 0.1) {
                    angular = stickX;
                }

                if (standard) {
                    // Xbox-style: Start pauses, A(0)/RT(7) thrust, X(2)/LT(6) fire.
                    const startNow = btn(9);
                    if (startNow && !this.startWasDown) {
                        this.startWasDown = true;
                        this.level.pauseGame();
                        return;
                    }
                    this.startWasDown = startNow;

                    if (btn(0) || btnVal(7) > 0.1) {
                        thrust = true;
                    }
                    if (btn(2) || btn(6)) {
                        fire = true;
                    }
                } else {
                    // Simple joystick (e.g. Quickshot): no standard button layout,
                    // so push the stick DOWN to thrust and let any/all buttons
                    // fire. (No Start button - pause with Esc/P.)
                    if (axis(1) > 0.5) {
                        thrust = true;
                    }
                    if (pad.buttons.some(b => b && b.pressed)) {
                        fire = true;
                    }
                }
            }
        }

        // --- Apply the combined intent once ---

        // Rotation (+ its sound). angular is -1..1; keyboard gives full deflection.
        if (angular < 0) {
            this.sprite.setAngularVelocity(shipAngularVelocity * angular);
            this.playDirectionalSound(this.sfx_left);
        } else if (angular > 0) {
            this.sprite.setAngularVelocity(shipAngularVelocity * angular);
            this.playDirectionalSound(this.sfx_right);
        } else {
            this.sprite.setAngularVelocity(0);
            this.stopDirectionalSounds();
        }

        // Thrust (+ its sound + the engine-glow animation).
        if (thrust) {
            const acceleration = this.level.physics.velocityFromRotation(this.sprite.rotation, shipAcceleration);
            this.sprite.setAcceleration(acceleration.x, acceleration.y);
            this.playThrustSound();
            this.sprite.play('ignite', true);
        } else {
            this.sprite.setAcceleration(0, 0);
            this.stopThrustSound();
            this.sprite.play('ignite', false);
        }

        // Fire
        if (fire && time > this.level.laserInterval) {
            this.fireLaser(time);
        }
    }

    // Record this frame's pose so next frame's collision check can sweep the gap
    // between the two poses (anti-tunnelling). Called at the end of the update.
    recordPose() {
        this.prevX = this.sprite.x;
        this.prevY = this.sprite.y;
        this.prevAngle = this.sprite.angle;
    }

    playDirectionalSound(sound: Phaser.Sound.BaseSound) {
        if (!sound.isPlaying) {
            sound.play({ loop: true, volume: 0.2 * this.level.sfxVol() });
        }
    }

    stopDirectionalSounds() {
        this.sfx_left.stop();
        this.sfx_right.stop();
    }

    playThrustSound() {
        if (!this.sfx_thrust.isPlaying) {
            this.sfx_thrust.play({ loop: true, volume: 0.6 * this.level.sfxVol() });
        }
    }

    stopThrustSound() {
        this.sfx_thrust.stop();
    }

    fireLaser(time: number) {
        const laser = this.lasers.get(this.sprite.x, this.sprite.y, 'redlaser') as LaserImage;
        if (laser) {
            laser.setActive(true).setVisible(true);
            laser.setAngle(this.sprite.angle);
            this.level.physics.velocityFromRotation(this.sprite.rotation, laserSpeed, laser.body.velocity);

            // Set a timer to deactivate the laser after laserLifespan
            laser.removeTimer = this.level.time.delayedCall(laserLifespan, () => {
                if (laser.active) {
                    this.level.removeLaser(laser);
                }
            });

            this.sfx_laser.play();
            this.level.laserInterval = time + laserGap;
        }
    }

    // Get ship polygon based on current position and rotation
    getShipPolygon(ship: Phaser.Physics.Arcade.Sprite): Phaser.Geom.Polygon {
        return this.getShipPolygonAt(ship.x, ship.y, ship.angle);
    }

    // Build the ship polygon at an arbitrary position and angle (degrees).
    // Used both for the current pose and for the swept positions sampled along
    // the ship's path of travel (see preciseShipCollision).
    getShipPolygonAt(x: number, y: number, angleDeg: number): Phaser.Geom.Polygon {
        const points: Phaser.Geom.Point[] = this.polygonBase.map(p => new Phaser.Geom.Point(p.x, p.y));

        const rotation = Phaser.Math.DegToRad(angleDeg);
        for (let i = 0; i < points.length; i++) {
            // First rotate around origin
            Phaser.Math.Rotate(points[i], rotation);
            // Then translate to the given position
            points[i].x += x;
            points[i].y += y;
        }

        return new Phaser.Geom.Polygon(points);
    }

    // Process callback for the ship/asteroid overlap (pure predicate, no side
    // effects). The damage and bounce are handled by shipAsteroidBounce.
    preciseShipCollision(ship: Phaser.Physics.Arcade.Sprite, asteroid: Phaser.Physics.Arcade.Image): boolean {
        return this.shipSweptHits(ship, asteroid, this.level.getAsteroidPolygon(asteroid));
    }

    // Process callback for the ship/enemy overlap (pure predicate, no side
    // effects). The bounce and sound are handled by shipEnemyBounce.
    preciseEnemyCollision(ship: Phaser.Physics.Arcade.Sprite, enemy: Phaser.Physics.Arcade.Sprite): boolean {
        return this.shipSweptHits(ship, enemy, this.level.enemies.getEnemyPolygon(enemy));
    }

    // Returns true only when the ship's polygon actually touches the obstacle's
    // polygon, so contact is reported on real shape contact rather than on
    // bounding-box overlap. To stop a fast ship tunnelling through an obstacle
    // (arcade physics only checks discrete frames), this sweeps the ship polygon
    // back along the path it travelled since the previous frame.
    shipSweptHits(ship: Phaser.Physics.Arcade.Sprite, obstacle: Phaser.Physics.Arcade.Image, obstaclePolygon: Phaser.Geom.Polygon): boolean {
        // How far did the ship move since last frame? (Falls back to the
        // current pose on the very first frame, before a previous pose exists.)
        const prevX = (this.prevX !== undefined) ? this.prevX : ship.x;
        const prevY = (this.prevY !== undefined) ? this.prevY : ship.y;
        const prevAngle = (this.prevAngle !== undefined) ? this.prevAngle : ship.angle;
        const moveDist = Phaser.Math.Distance.Between(prevX, prevY, ship.x, ship.y);

        // Quick reject: skip if the obstacle is farther than the ship could
        // possibly reach this frame (combined radii plus the distance moved).
        const distance = Phaser.Math.Distance.Between(ship.x, ship.y, obstacle.x, obstacle.y);
        const combinedRadii = (ship.width + obstacle.width) * 0.5;
        if (distance > combinedRadii + moveDist) {
            return false;
        }

        // Sample the ship along its path (current pose back to last frame's
        // pose), roughly every 6 pixels of travel, and test each pose.
        const samples = Math.max(1, Math.ceil(moveDist / 6));
        for (let i = 0; i <= samples; i++) {
            const t = i / samples;
            const x = ship.x + (prevX - ship.x) * t;
            const y = ship.y + (prevY - ship.y) * t;
            const angle = ship.angle + (prevAngle - ship.angle) * t;
            const shipPolygon = this.getShipPolygonAt(x, y, angle);
            if (this.level.polygonsIntersect(shipPolygon, obstaclePolygon)) {
                return true;
            }
        }

        return false;
    }

    shipDestroyed() {
        if (this.level.alive) {
            this.level.alive = false;

            // Stop all ship-related sounds
            this.silence();

            // Play explosion sound
            this.sfx_explode.play();

            // Play explosion animation
            this.sprite.play('explode');

            // Listen for animation completion
            this.sprite.on('animationcomplete-explode', this.onExplosionComplete, this);
        }
    }

    onExplosionComplete() {
        // Remove the ship from the scene
        this.sprite.destroy();

        // Call game over method
        this.level.gameOver();
    }

    shipHit(sound: Phaser.Sound.BaseSound) {
        if (this.level.time.now > this.level.nextHit) {
            if (this.level.shield == 0) {
                this.shipDestroyed();
            }
            else {
                sound.play();
                this.level.shield = this.level.shield - 1;
                this.level.updateUI();
                this.level.nextHit = this.level.time.now + shieldDuration;
            }
        }
    }

    // Bounce the ship off an obstacle's centre: reflect its velocity about the
    // collision normal (obstacle centre -> ship centre), optionally push the
    // obstacle, and nudge the ship clear so it doesn't stay embedded and
    // re-trigger. Returns true if the ship was actually moving into the obstacle
    // (a real impact this frame), so callers can gate one-shot effects like
    // sounds. Shared by the asteroid and enemy-ship collision callbacks.
    bounceShipOff(ship: Phaser.Physics.Arcade.Sprite, other: Phaser.Physics.Arcade.Image, otherPushFactor: number): boolean {
        // Collision normal: from the obstacle centre out to the ship centre.
        let nx = ship.x - other.x;
        let ny = ship.y - other.y;
        const len = Math.sqrt(nx * nx + ny * ny) || 1;
        nx /= len;
        ny /= len;

        const vx = ship.body.velocity.x;
        const vy = ship.body.velocity.y;
        const vDotN = vx * nx + vy * ny;
        const approaching = vDotN < 0;

        // Only react if the ship is actually moving into the obstacle.
        if (approaching) {
            // Reflect the velocity's normal component (elastic when e = 1).
            const e = shipBounceRestitution;
            ship.body.velocity.x = vx - (1 + e) * vDotN * nx;
            ship.body.velocity.y = vy - (1 + e) * vDotN * ny;

            // Give the obstacle a nudge in the direction it was struck.
            if (other.body && otherPushFactor > 0) {
                other.body.velocity.x += -nx * (-vDotN) * otherPushFactor;
                other.body.velocity.y += -ny * (-vDotN) * otherPushFactor;
            }
        }

        // Push the ship out along the normal so it separates this frame.
        ship.x += nx * 4;
        ship.y += ny * 4;

        return approaching;
    }

    // Overlap callback for the ship hitting an asteroid. We bounce manually (see
    // bounceShipOff) rather than rely on arcade's AABB separation.
    shipAsteroidBounce(ship: Phaser.Physics.Arcade.Sprite, asteroid: Phaser.Physics.Arcade.Image) {
        this.shipHit(this.sfx_collide); // damage + sound (has its own cooldown)
        const mass = asteroid.getData('mass') || 1;
        this.bounceShipOff(ship, asteroid, 0.4 / mass);
    }

    // Overlap callback for the ship colliding with an enemy ship. Ramming bounces
    // and plays the collide sound but does not drain shields - the enemies' fire
    // is the threat - matching the original game.
    shipEnemyBounce(ship: Phaser.Physics.Arcade.Sprite, enemy: Phaser.Physics.Arcade.Sprite) {
        if (this.bounceShipOff(ship, enemy, 0.5)) {
            this.level.enemies.playCollide();
        }
    }

    // Overlap callback for enemy fire hitting the ship.
    enemyHit(ship: Phaser.Physics.Arcade.Sprite, enemyfire: Phaser.Physics.Arcade.Image) {
        this.shipHit(this.sfx_collide);
        this.level.removeLaser(enemyfire as LaserImage);
    }
}
